import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WifiIcon from '@mui/icons-material/Wifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Two distinct reference examples with different content & banners
const MODAL_REFERENCE_EXAMPLES = [
  {
    title: 'Pray for Christians (21 Days Fasting & Prayer)',
    bannerImage: '/assets/images/example-prayer-banner.png',
    duration: 21,
    format: 'Personal',
    description: 'As followers of Jesus, we are called to mature in faith. Let us pray for Christians to develop a strong personal walk with God, seeking Him daily through prayer and Scripture.',
  },
  {
    title: '40 Days Healing, Restoration & Revival',
    bannerImage: '/prayforthenation/template4.jpg',
    duration: 40,
    format: 'Group',
    description: 'Uniting in 40 days of earnest prayer for divine healing across our communities, spiritual renewal of broken families, and widespread awakening in the next generation.',
  },
];

// Helper to strip HTML tags for clean display or render safely
function renderDescriptionContent(htmlOrText) {
  if (!htmlOrText || typeof htmlOrText !== 'string' || !htmlOrText.trim()) {
    return null;
  }
  const clean = htmlOrText.replace(/<p><br><\/p>/g, '').trim();
  if (!clean || clean === '<p></p>') return null;
  return clean;
}

export default function MobileSpecialPrayerModal({
  open = false,
  onClose,
  isExample = false,
  title = '',
  bannerImage = '',
  duration = 40,
  format = 'Personal',
  description = '',
}) {
  const [exampleIndex, setExampleIndex] = useState(0);
  const curExample = MODAL_REFERENCE_EXAMPLES[exampleIndex] || MODAL_REFERENCE_EXAMPLES[0];

  // Content for Example mode vs Live mode
  const effectiveTitle = isExample
    ? curExample.title
    : (title && title.trim() ? title : 'Your Prayer Campaign Title');

  const effectiveBanner = isExample
    ? curExample.bannerImage
    : bannerImage;

  const effectiveDuration = isExample
    ? `${curExample.duration} Days`
    : `${duration || 40} Days`;

  const effectiveFormat = isExample
    ? curExample.format.toUpperCase()
    : ((format || 'Personal').toUpperCase() === 'GROUP' ? 'GROUP PRAYER' : (format || 'Personal').toUpperCase());

  const effectiveDescription = isExample
    ? curExample.description
    : renderDescriptionContent(description);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '44px',
          bgcolor: '#070B11',
          p: 0,
          boxShadow: '0 25px 65px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.08)',
          border: '8px solid #1E293B',
          overflow: 'hidden',
          width: '375px',
          maxWidth: '95vw',
          height: '740px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          m: 'auto',
        },
      }}
    >
      {/* ── TOP STATUS BAR (Android/iOS Phone Status Bar) ── */}
      <Box
        sx={{
          bgcolor: '#0C131A',
          px: 2.5,
          pt: 1.2,
          pb: 0.8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#E2E8F0',
          fontSize: '0.82rem',
          fontWeight: 700,
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <span>14:49</span>

        {/* Punch Hole Camera Cutout */}
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: '#000000',
            border: '2px solid #334155',
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <SignalCellularAltIcon sx={{ fontSize: 16, color: '#E2E8F0' }} />
          <WifiIcon sx={{ fontSize: 16, color: '#E2E8F0' }} />
          <BatteryFullIcon sx={{ fontSize: 18, color: '#E2E8F0' }} />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#94A3B8',
              p: 0.2,
              ml: 0.5,
              '&:hover': { color: '#EF4444' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Top Notice Bar (Reference Example vs Live Preview) ── */}
      <Box
        sx={{
          bgcolor: isExample ? '#064E3B' : '#134E4A',
          borderBottom: '1.5px solid',
          borderColor: isExample ? '#10B981' : '#14B8A6',
          px: 2,
          py: 0.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: '#ECFDF5', fontSize: '0.76rem', fontWeight: 700 }}>
          {isExample ? `✨ User Example (${exampleIndex + 1}/2)` : '🟢 Live User Preview'}
        </Typography>
        {isExample ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button
              size="small"
              onClick={() => setExampleIndex(prev => (prev === 0 ? 1 : 0))}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#FFFFFF',
                px: 1,
                py: 0.2,
                borderRadius: '12px',
                fontSize: '0.68rem',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
              }}
            >
              {exampleIndex === 0 ? 'Next Example →' : '← 1st Example'}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ color: 'rgba(236, 253, 245, 0.75)', fontSize: '0.7rem', fontWeight: 600 }}>
            Mobile App
          </Typography>
        )}
      </Box>

      {/* ── Scrollable Phone Screen Content ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: '#0B1319',
          p: 2,
          color: '#FFFFFF',
          boxSizing: 'border-box',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
        }}
      >
        {/* App Navigation Bar inside mobile */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ArrowBackIcon sx={{ color: '#FFFFFF', fontSize: 18 }} />
            </Box>
            <Typography
              noWrap
              sx={{
                fontSize: '1.02rem',
                fontWeight: 700,
                color: '#FFFFFF',
                pr: 0.5,
              }}
            >
              {effectiveTitle}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.6} flexShrink={0}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShareOutlinedIcon sx={{ color: '#FFFFFF', fontSize: 16 }} />
            </Box>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <QrCodeScannerIcon sx={{ color: '#FFFFFF', fontSize: 16 }} />
            </Box>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GroupOutlinedIcon sx={{ color: '#FFFFFF', fontSize: 16 }} />
            </Box>
          </Box>
        </Box>

        {/* Banner Image */}
        <Box
          sx={{
            width: '100%',
            height: 185,
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            mb: 1.75,
          }}
        >
          {effectiveBanner ? (
            <img
              src={effectiveBanner}
              alt={effectiveTitle}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              onError={(e) => {
                if (!e.currentTarget.src.includes('/prayforthenation/template1.jpg')) {
                  e.currentTarget.src = '/prayforthenation/template1.jpg';
                  return;
                }
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextSibling) {
                  e.currentTarget.nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
          {isExample && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.2,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(6px)',
                borderRadius: '14px',
                px: 0.6,
                py: 0.2,
                border: '1px solid rgba(255,255,255,0.25)',
                zIndex: 2,
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExampleIndex(prev => (prev === 0 ? 1 : 0));
                }}
                sx={{ p: 0.1, color: '#2DD4BF' }}
              >
                <ChevronLeftIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.68rem', fontWeight: 800, px: 0.2 }}>
                {exampleIndex + 1}/2
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExampleIndex(prev => (prev === 0 ? 1 : 0));
                }}
                sx={{ p: 0.1, color: '#2DD4BF' }}
              >
                <ChevronRightIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
          <Box
            sx={{
              display: effectiveBanner ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              background: 'linear-gradient(135deg, rgba(37, 105, 116, 0.25) 0%, rgba(15, 23, 42, 0.6) 100%)',
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', mb: 0.3 }}>
              Banner Image
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.74rem' }}>
              Select a template or upload an image to preview here
            </Typography>
          </Box>
        </Box>

        {/* Title & Three-dots Menu */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mb={1.5}
        >
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: '#FFFFFF',
              lineHeight: 1.25,
              flex: 1,
            }}
          >
            {effectiveTitle}
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MoreHorizIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
          </Box>
        </Box>

        {/* Badges Row (Days, Format, Audio) */}
        <Box display="flex" alignItems="center" gap={0.8} mb={1.75}>
          {/* Duration badge */}
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              backgroundColor: '#14222B',
              borderRadius: '18px',
              px: 1.4,
              py: 0.5,
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <AccessTimeIcon sx={{ color: '#94A3B8', fontSize: 14 }} />
            <Typography sx={{ color: '#94A3B8', fontSize: '0.76rem', fontWeight: 600 }}>
              {effectiveDuration}
            </Typography>
          </Box>

          {/* Format badge */}
          <Box
            sx={{
              backgroundColor: '#133842',
              borderRadius: '18px',
              px: 1.4,
              py: 0.5,
              border: '1px solid rgba(56, 189, 248, 0.25)',
            }}
          >
            <Typography
              sx={{
                color: '#38BDF8',
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.5px',
              }}
            >
              {effectiveFormat}
            </Typography>
          </Box>

          {/* Audio button */}
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              backgroundColor: '#14222B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <VolumeUpOutlinedIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
          </Box>
        </Box>

        {/* Primary Action Button: Start Praying */}
        <Button
          fullWidth
          startIcon={<WhatshotIcon sx={{ color: '#FFFFFF', fontSize: 19 }} />}
          sx={{
            height: 44,
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #3B828E 0%, #2D727E 100%)',
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.94rem',
            textTransform: 'none',
            mb: 1.75,
            boxShadow: '0 4px 14px rgba(45, 114, 126, 0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4899A6 0%, #358390 100%)',
            },
          }}
        >
          Start Praying
        </Button>

        {/* Description Card (No Prayer Categories) */}
        <Box
          sx={{
            backgroundColor: '#13222B',
            borderRadius: '16px',
            p: 2,
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <Box display="flex" alignItems="center" gap={0.8} mb={1}>
            <DescriptionOutlinedIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
            <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.88rem' }}>
              Description
            </Typography>
          </Box>
          {effectiveDescription ? (
            <Typography
              sx={{
                color: '#CBD5E1',
                fontSize: '0.84rem',
                lineHeight: 1.6,
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: effectiveDescription }}
            />
          ) : (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.45)',
                fontSize: '0.8rem',
                fontStyle: 'italic',
              }}
            >
              Enter a prayer description in the form to see it rendered here.
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Phone Bottom Navigation Indicator Bar ── */}
      <Box
        sx={{
          bgcolor: '#0C131A',
          py: 1.2,
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.35)',
          }}
        />
      </Box>
    </Dialog>
  );
}
